import { Routes } from '@angular/router';
import { permissionRouteData, permissionRouteDataAny } from 'shared/utils/permission.util';
import { authGuard } from 'shared/guards/auth.guard';
import { alreadyAuthGuard } from 'shared/guards/already-auth.guard';
import { permissionGuard } from 'shared/guards/permission.guard';
import { superAdminGuard } from 'shared/guards/super-admin.guard';
import { accessManagementGuard } from 'shared/guards/access-management.guard';
import { tvDeviceGuard } from 'shared/guards/tv-device.guard';

export const viewRoutes: Routes = [
  {
    path: 'tv',
    pathMatch: 'full',
    canActivate: [tvDeviceGuard],
    loadComponent: () => import('./tv/tv.view'),
  },
  {
    path: 'tv/reproducir',
    canActivate: [tvDeviceGuard],
    loadComponent: () => import('./tv-player/tv-player.view'),
  },
  {
    path: 'tv/:code',
    canActivate: [authGuard],
    loadComponent: () => import('./tv-pairing/tv-pairing.view'),
  },
  {
    path: 'iniciar-sesion',
    canActivate: [alreadyAuthGuard],
    loadComponent: () => import('./session/session.view'),
  },
  {
    path: 'cambiar-contrasena-obligatoria',
    canActivate: [authGuard],
    loadComponent: () => import('./mandatory-password-change/mandatory-password-change.view'),
  },
  {
    path: 'consentimiento-legal',
    canActivate: [authGuard],
    loadComponent: () => import('./legal-consent/legal-consent.view'),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./home/home.view'),
    children: [
      {
        path: 'roles',
        canActivate: [permissionGuard],
        data: permissionRouteData('ROLE_LIST'),
        loadComponent: () => import('./role/role.view'),
      },
      {
        path: 'acciones',
        canActivate: [permissionGuard],
        data: permissionRouteData('ACTION_LIST'),
        loadComponent: () => import('./action/action.view'),
      },
      {
        path: 'permisos',
        canActivate: [permissionGuard],
        data: permissionRouteData('PERMISSION_LIST'),
        loadComponent: () => import('./permission/permission.view'),
      },
      {
        path: 'gestion-accesos',
        canActivate: [accessManagementGuard],
        data: permissionRouteData('ACCESS_MANAGEMENT_LIST'),
        loadComponent: () => import('./access-management/access-management.view'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'usuarios',
          },
          {
            path: 'usuarios',
            canActivate: [superAdminGuard],
            data: permissionRouteData('ACCESS_MANAGEMENT_LIST'),
            loadComponent: () => import('./access-management/access-management-users.view'),
          },
          {
            path: 'roles',
            canActivate: [permissionGuard],
            data: permissionRouteData('ACCESS_MANAGEMENT_LIST'),
            loadComponent: () => import('./access-management/access-management-legacy-role-redirect.view'),
          },
        ],
      },
      {
        path: 'accesos-roles',
        canActivate: [permissionGuard],
        data: permissionRouteDataAny(['ACCESS_MANAGEMENT_LIST', 'ROLE_ACCESS_MANAGEMENT_LIST']),
        loadComponent: () => import('./access-management/access-management-legacy-role-redirect.view'),
      },
      {
        path: 'clinicas',
        canActivate: [permissionGuard],
        data: permissionRouteData('CLINIC_LIST'),
        loadComponent: () => import('./clinic/clinic.view'),
      },
      {
        path: 'pacientes',
        canActivate: [permissionGuard],
        data: permissionRouteData('PATIENT_LIST'),
        loadComponent: () => import('./patient/patient.view'),
      },
      {
        path: 'pacientes/:id',
        canActivate: [permissionGuard],
        data: permissionRouteData('PATIENT_LIST'),
        loadComponent: () => import('./patient-detail/patient-detail.view'),
      },
      {
        path: 'pacientes/:id/sesion-activa',
        canActivate: [permissionGuard],
        data: permissionRouteData('TREATMENT_LIST'),
        loadComponent: () => import('./patient-active-session/patient-active-session.view'),
      },
      {
        path: 'pacientes/:patientId/sesiones/:treatmentId/estimulos/:id/reproducir',
        canActivate: [permissionGuard],
        data: permissionRouteData('TREATMENT_LIST'),
        loadComponent: () => import('./stimulus-player/stimulus-player.view'),
      },
      {
        path: 'pacientes/:patientId/tratamientos/:treatmentId/estimulos/:id/reproducir',
        canActivate: [permissionGuard],
        data: { ...permissionRouteData('TREATMENT_LIST'), sessionRedirect: 'patient-detail-playback' },
        loadComponent: () => import('./legacy-session-route-redirect.view'),
      },
      {
        path: 'profesionales',
        canActivate: [permissionGuard],
        data: permissionRouteData('PROFESSIONAL_LIST'),
        loadComponent: () => import('./professional/professional.view'),
      },
      {
        path: 'diagnosticos',
        canActivate: [permissionGuard],
        data: permissionRouteData('DIAGNOSIS_LIST'),
        loadComponent: () => import('./diagnosis/diagnosis.view'),
      },
      {
        path: 'sesiones',
        canActivate: [permissionGuard],
        data: permissionRouteData('TREATMENT_LIST'),
        loadComponent: () => import('./treatment/treatment.view'),
      },
      {
        path: 'tratamientos',
        canActivate: [permissionGuard],
        data: { ...permissionRouteData('TREATMENT_LIST'), sessionRedirect: 'staff-list' },
        loadComponent: () => import('./legacy-session-route-redirect.view'),
      },
      {
        path: 'estimulos',
        canActivate: [permissionGuard],
        data: permissionRouteData('STIMULUS_LIST'),
        loadComponent: () => import('./stimulus/stimulus.view'),
      },
      {
        path: 'estimulos/masiva',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./stimulus/stimulus-bulk-upload.view'),
      },
      {
        path: 'suscripciones/monedas',
        canActivate: [superAdminGuard],
        data: permissionRouteData('SUBSCRIPTION_CURRENCY_LIST'),
        loadComponent: () => import('./subscription-currency/subscription-currency.view'),
      },
      {
        path: 'suscripciones/planes',
        canActivate: [superAdminGuard],
        data: permissionRouteData('SUBSCRIPTION_PLAN_LIST'),
        loadComponent: () => import('./subscription-plan/subscription-plan.view'),
      },
      {
        path: 'suscripciones/clinicas',
        canActivate: [superAdminGuard],
        data: permissionRouteData('CLINIC_SUBSCRIPTION_LIST'),
        loadComponent: () => import('./clinic-subscription/clinic-subscription.view'),
      },
      {
        path: 'estimulos/:id/reproducir',
        canActivate: [permissionGuard],
        data: permissionRouteData('STIMULUS_LIST'),
        loadComponent: () => import('./stimulus-player/stimulus-player.view'),
      },
      {
        path: 'patient-portal',
        canActivate: [permissionGuard],
        data: permissionRouteData('PATIENT_OVERVIEW'),
        loadComponent: () => import('./patient-portal/patient-portal-home.view'),
      },
      {
        path: 'patient-portal/diagnoses',
        canActivate: [permissionGuard],
        data: permissionRouteData('PATIENT_DIAGNOSES'),
        loadComponent: () => import('./patient-portal-diagnoses/patient-portal-diagnoses.view'),
      },
      {
        path: 'patient-portal/sesiones',
        canActivate: [permissionGuard],
        data: permissionRouteData('PATIENT_TREATMENTS'),
        loadComponent: () => import('./patient-portal-treatments/patient-portal-treatments.view'),
      },
      {
        path: 'patient-portal/treatments',
        canActivate: [permissionGuard],
        data: { ...permissionRouteData('PATIENT_TREATMENTS'), sessionRedirect: 'patient-portal-list' },
        loadComponent: () => import('./legacy-session-route-redirect.view'),
      },
      {
        path: 'patient-portal/estimulos/:id/reproducir',
        canActivate: [permissionGuard],
        data: { ...permissionRouteData('PATIENT_TREATMENTS'), playbackContext: 'patient-portal' },
        loadComponent: () => import('./stimulus-player/stimulus-player.view'),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
]
